import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import SplashScreen from './components/SplashScreen';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/Login';
import Home from './pages/Home';
import Equipo from './pages/Equipo';
import Estadisticas from './pages/Estadisticas';
import Captacion from './pages/Captacion';
import Perfil from './pages/Perfil';
import { PartidosAsignados, CrearInforme, TusInformes, Jornadas } from './pages';
import { Disponibilidad } from './pages/Disponibilidad';
import TuPlantilla from './pages/TuPlantilla';
import  Asistencia from './pages/Asistencia';
import { CambioPasswordModal } from './components/modals/CambioPasswordModal';

/**
 * Componente de rutas protegidas
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user, refreshUser, logout } = useAuth();
  
  // Mostrar SplashScreen mientras se verifica la autenticación
  if (isLoading) {
    return (
      <SplashScreen 
        isLoading={true} 
        message="Verificando sesión..." 
      />
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Cambio de contraseña obligatorio: no se carga ninguna pantalla (el backend las bloquea igualmente)
  if (user?.passwordChangeRequired) {
    return (
      <div className="min-h-screen bg-slate-950">
        <CambioPasswordModal
          isOpen
          required
          onClose={refreshUser}
          onLogout={logout}
        />
      </div>
    );
  }

  return <MainLayout>{children}</MainLayout>;
};

/**
 * Componente de rutas de la aplicación
 */
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Ruta de login sin layout */}
      <Route path="/login" element={<Login />} />
      
      {/* Rutas protegidas con layout */}
      <Route path="/" element={
        <ProtectedRoute>
          <Home />
        </ProtectedRoute>
      } />
      
      <Route path="/equipo" element={
        <ProtectedRoute>
          <Equipo />
        </ProtectedRoute>
      } />
      
      <Route path="/captacion" element={
        <ProtectedRoute>
          <Captacion />
        </ProtectedRoute>
      } />
      
      <Route path="/perfil" element={
        <ProtectedRoute>
          <Perfil />
        </ProtectedRoute>
      } />
      
      <Route path="/partidos" element={
        <ProtectedRoute>
          <PartidosAsignados />
        </ProtectedRoute>
      } />
      
      <Route path="/crear-informe" element={
        <ProtectedRoute>
          <CrearInforme />
        </ProtectedRoute>
      } />

      <Route path="/tus-informes" element={
        <ProtectedRoute>
          <TusInformes />
        </ProtectedRoute>
      } />
      
      <Route path="/disponibilidad" element={
        <ProtectedRoute>
          <Disponibilidad />
        </ProtectedRoute>
      } />
      
      <Route path="/tu-plantilla" element={
        <ProtectedRoute>
          <TuPlantilla />
        </ProtectedRoute>
      } />

      <Route path="/asistencia" element={
        <ProtectedRoute>
          <Asistencia />
        </ProtectedRoute>
      } />

       <Route path="/jornadas" element={
        <ProtectedRoute>
          <Jornadas />
        </ProtectedRoute>
      } /> 
      <Route path="/estadisticas" element={
        <ProtectedRoute>
          <Estadisticas />
        </ProtectedRoute>
      } /> 
      
      {/* Redirección por defecto */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // No recargar al cambiar de ventana por defecto
      retry: 1,
    },
  },
});

/**
 * Componente principal de la aplicación con SplashScreen inicial
 */
const App: React.FC = () => {
  const [appInitialized, setAppInitialized] = React.useState(false);

  React.useEffect(() => {
    // Simular carga inicial de la aplicación
    const timer = setTimeout(() => {
      setAppInitialized(true);
    }, 1500); // 1.5 segundos para mostrar el splash inicial

    return () => clearTimeout(timer);
  }, []);

  // Mostrar SplashScreen durante la carga inicial de la app
  if (!appInitialized) {
    return (
      <SplashScreen 
        isLoading={true} 
        message="Cargando CorneApp..." 
      />
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <div className="App">
            <AppRoutes />
          </div>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;